import { useTranslation } from "react-i18next";
import type {
  ObjectKind,
  SchemaElement,
} from "../../api/restaurantSchema";
import { BLOCKS, blockDepthPx, blockShadow } from "./blockDepth";
import { ELEMENT_STYLES } from "./elementCatalog";
import EntranceDoor from "./EntranceDoor";
import { DESIGN_UNIT, objectDimensions } from "./geometry";
import SelectionHandles from "./SelectionHandles";
import { SELECTION_RING } from "./tableTones";

// Стената е тънка: в редактора се хваща в ивица около нея поне толкова висока
// (пиксели) и толкова по-дълга от всеки край — иначе трудно се улучва за местене.
// Стените са най-долният слой, затова ивицата не скрива маса/обект до тях.
const WALL_GRAB_HEIGHT_PX = 48;
const WALL_GRAB_END_PX = 12;

/**
 * Обект отгоре по дизайна — блокче със страна и сянка върху пода, като
 * масите. Стената е плътна ивица (дълга `length`; в редактора — с невидима
 * по-широка ивица за хващане, а краищата ѝ са дръжки за разпъване в
 * `SchemaCanvas`); входът е врата (`EntranceDoor`); останалите са тонални
 * плочки с икона и надпис. Размерите са логически единици × `scale` — inline
 * style само за тях (изключение (1)).
 */
const ObjectView = ({
  element,
  scale,
  selected,
  grabbable = false,
}: {
  element: SchemaElement;
  scale: number;
  selected: boolean;
  /** Елементът се хваща и мести (редакторът) — стената получава ивица за хващане. */
  grabbable?: boolean;
}) => {
  const { t } = useTranslation();
  const kind = element.kind as ObjectKind;
  const designPixel = DESIGN_UNIT * scale;

  if (kind === "entrance") {
    return <EntranceDoor element={element} scale={scale} selected={selected} />;
  }

  const style = ELEMENT_STYLES[kind];
  const { width, height } = objectDimensions(kind, element.size, element.length);

  if (kind === "wall") {
    const wallHeight = Math.max(3, height * scale);
    // Колко стърчи ивицата за хващане над/под стената (пиксели, по време на изпълнение).
    const grabOverhang = Math.max(0, (WALL_GRAB_HEIGHT_PX - wallHeight) / 2);
    return (
      <div
        className={`relative bg-schema-wall ${
          selected ? "outline outline-[2.5px] outline-offset-2 outline-brand" : ""
        }`}
        style={{
          width: width * scale,
          height: wallHeight,
          borderRadius: designPixel,
          boxShadow: blockShadow({
            rotation: element.rotation,
            depth: blockDepthPx(BLOCKS.wall.depth, designPixel),
            color: BLOCKS.wall.side,
          }),
        }}
      >
        {grabbable && (
          // Посочена — лека подсветка: личи, че стената се хваща и оттук.
          <span
            aria-hidden
            className="absolute rounded-full transition-colors duration-150 hover:bg-brand/10"
            style={{
              top: -grabOverhang,
              bottom: -grabOverhang,
              left: -WALL_GRAB_END_PX,
              right: -WALL_GRAB_END_PX,
            }}
          />
        )}
      </div>
    );
  }

  const isPlant = kind === "plant";
  return (
    <div
      className={`relative flex items-center justify-center gap-2 border-[1.5px] font-bold transition-[border-color,box-shadow] duration-150 ${style.bodyClass} ${
        isPlant ? "rounded-full" : ""
      } ${selected ? "!border-brand" : ""}`}
      style={{
        width: width * scale,
        height: height * scale,
        borderRadius: isPlant ? undefined : 12 * designPixel,
        fontSize: Math.max(10, 15 * designPixel),
        boxShadow: blockShadow({
          rotation: element.rotation,
          depth: blockDepthPx(BLOCKS[kind].depth, designPixel),
          color: BLOCKS[kind].side,
          ring: selected ? SELECTION_RING : null,
        }),
      }}
    >
      <div
        className="flex items-center gap-[0.5em]"
        style={{ transform: `rotate(${-element.rotation}deg)` }}
      >
        <style.Icon
          className={isPlant ? "h-[1.35em] w-[1.35em]" : "h-[1.2em] w-[1.2em]"}
        />
        {!isPlant && width * scale > 60 && (
          <span className="whitespace-nowrap">{t(style.labelKey)}</span>
        )}
      </div>
      {selected && <SelectionHandles />}
    </div>
  );
};

export default ObjectView;
